import { IOptionalPagination, IPagedListResponse } from '@edgebox/data-definition-kit';
import { TaskDomain, TaskGroupType } from '@edgebox/sync-core-data-definitions';
import { ClientSiteEntity, ClientTaskGroupEntity, ITaskGroupListFilters } from '@edgebox/sync-core-rest-client';
import { ResumableOnboarding } from './onboardingContinueLabel.utils';

/**
 * Loading the onboardings a customer can resume, and the domain each one is about.
 *
 * It lives beside the screen rather than inside it because the screen imports ESM-only UI packages
 * that the Jest transform does not process — importing it from a test fails before a single
 * assertion runs. This path shipped a user-visible regression once; it has to be testable.
 *
 * Every type below is DERIVED from the client's own declarations rather than restated, so a rename
 * or a shape change upstream breaks this build instead of silently changing what the button says.
 */

/**
 * What tells one onboarding from another of the same domain, taken from the client's own task-group
 * entity: when it started, when it last changed, and its status.
 */
export type OnboardingFacts = Pick<ClientTaskGroupEntity, 'createdAt' | 'updatedAt' | 'status'>;

/** An onboarding a customer can resume, exactly as the screen renders it. */
export type IExistingOnboardingTaskGroup = ResumableOnboarding & OnboardingFacts;

/**
 * The site fields this module reads, taken from the client's own site entity.
 *
 * `Pick` rather than a fresh interface: if `baseUrl` is renamed or retyped upstream, this stops
 * compiling. It also bounds what the resolver is allowed to look at — see `resolveDomainFromSite`.
 */
export type OnboardingSite = Pick<ClientSiteEntity, 'baseUrl' | 'name'>;

/**
 * The task-group fields this module reads.
 *
 * A structural slice, not a restatement: `ClientTaskGroupEntity` is assignable to it — its `id` is a
 * branded `InternalId`, which is a `string`, and its `site` is an `IRuntimeReference<ClientSiteEntity>`
 * whose `get()` resolves something wider than `OnboardingSite`. The screen passes the real client,
 * so the compiler checks that assignability on every build; and a test can build a value of this
 * type honestly, instead of casting a stub into an entity it is not.
 */
export interface OnboardingTaskGroup extends OnboardingFacts {
  id: string;
  site?: { get: () => Promise<OnboardingSite | undefined> };
}

/**
 * The single client call this module makes, as a typed slice of the API surface.
 *
 * Narrow on purpose: a test supplies exactly this and nothing else, with no cast, while the compiler
 * still verifies at the screen's call site that the real `ISyncCoreApi` provides it.
 */
export interface IOnboardingTaskGroupSource {
  service: {
    taskGroups: {
      list: (filters?: ITaskGroupListFilters, pagination?: IOptionalPagination) => Promise<IPagedListResponse<OnboardingTaskGroup>>;
    };
  };
}

function hostnameOf(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    return new URL(withProtocol).hostname || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The domain a site is about, or undefined when it names none.
 *
 * Reads `baseUrl` first and falls back to `name` — the two fields the site type declares as able to
 * carry one. The previous version took an `unknown` payload and probed four keys, `domain` and `url`
 * among them; neither exists on the site entity, so against typed data those two branches were
 * unreachable. They are deleted rather than kept as reassurance.
 */
export function resolveDomainFromSite(site: OnboardingSite | undefined): string | undefined {
  if (!site) {
    return undefined;
  }

  const candidates = [site.baseUrl, site.name].map((value) => value?.trim()).filter((value): value is string => !!value);

  for (const candidate of candidates) {
    const hostname = hostnameOf(candidate);
    if (hostname) {
      return hostname;
    }
  }

  // A name that is not URL-shaped is still the best label the site has for itself.
  return candidates[0];
}

export async function listExistingOnboardingTaskGroups(api: IOnboardingTaskGroupSource): Promise<IExistingOnboardingTaskGroup[]> {
  const itemsPerPage = 20;
  const allTaskGroups: OnboardingTaskGroup[] = [];

  for (let page = 0; page < 10; page += 1) {
    // Issued through the WRAPPED client, which is what hydrates `site` into a reference carrying a
    // `.get()`. The raw generated endpoint serializes `site` as a bare `{ id }`; the `.get()` call
    // below then quietly evaluated to `undefined` on every row — an optional call on a plain object
    // — and that is how the resume button lost the domain it names.
    //
    // The raw endpoint was used here to dodge the shared retry loop, whose stop-early guard read
    // `error.status` while the generated client reports `error.response.status`. That guard was
    // fixed in @edgebox/data-definition-kit 1.46.1 (`httpStatusOfError` reads both shapes), which
    // this repo already depends on, so a deterministic 4xx now rejects on the first attempt and
    // there is nothing left to dodge. `useResumableOnboardings.test.ts` drives the real loop with
    // the real error shape, so a regression there fails a test rather than a customer.
    const pageResult = await api.service.taskGroups.list(
      {
        domains: [TaskDomain.Governance],
        types: [TaskGroupType.Onboarding],
      },
      {
        page,
        itemsPerPage,
      }
    );

    allTaskGroups.push(...pageResult.items);

    if (pageResult.items.length < itemsPerPage) {
      break;
    }
  }

  const sortedTaskGroups = [...allTaskGroups].sort((left, right) => right.id.localeCompare(left.id));

  const taskGroupsWithDomain = await Promise.all(
    sortedTaskGroups.map(async (taskGroup): Promise<IExistingOnboardingTaskGroup | undefined> => {
      if (!taskGroup.id) {
        return undefined;
      }

      // A row whose site cannot be loaded must not take the others — or the list — down with it:
      // the resume button existing at all is what a customer with a started onboarding depends on.
      const site = await taskGroup.site?.get().catch(() => undefined);
      // Undefined, never a placeholder: "Continue with Unknown domain" reads as a fact about the
      // customer's project and is not one. The label decides what to say when it is absent.
      const domain = resolveDomainFromSite(site);
      const { createdAt, updatedAt, status } = taskGroup;

      return { id: taskGroup.id, createdAt, updatedAt, status, ...(domain ? { domain } : {}) };
    })
  );

  return taskGroupsWithDomain.filter((entry): entry is IExistingOnboardingTaskGroup => !!entry);
}
